import { Button, ButtonSize, ButtonVariant } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import React, { FC, useState } from 'react';

import { getTranslation, keys } from '../../../../Resources/Translations';
import { EmailValidationResult } from '../../../../Validation/EmailValidationResult.csharp';
import { EmailValidationStatus } from '../../../../Validation/EmailValidationStatus.csharp';
import { BlockSectionClassResolver } from '../../../BlockSection/BlockSectionClassResolver';
import { EmailInput } from '../../../Email/Check/EmailInput';
import { EmailSuggestionModal } from '../../../Email/Check/SuggestionModal/EmailSuggestionModal';
import { postFor } from '../../../ReactComponents/API';
import { RegexProvider } from '../../../ReactComponents/RegexProvider';
import { RenderHTML } from '../../../ReactComponents/RenderHTML';
import { NewsletterSubscriptionApiResponse } from './NewsletterSubscriptionApiResponse.csharp';
import { NewsletterSubscriptionProps } from './NewsletterSubscriptionProps.csharp';

export enum NewsletterSubscriptionBlockVariant {
    Default,
    OrderSummary
}

interface INewsletterSubscriptionProps extends NewsletterSubscriptionProps {
    variant: NewsletterSubscriptionBlockVariant;
    displayEmailInput: boolean;
    email: string;
}

export const NewsletterSubscription: FC<INewsletterSubscriptionProps> = (props) => {
    const {
        model: { headingText, consentText, successHeading, successMessage, subheadingText },
        signUpUrl
    } = props;

    const [email, setEmail] = useState<string>(props.email);
    const [consent, setConsent] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isRequestActive, setIsRequestActive] = useState<boolean>(false);
    const [showConsentIsRequired, setShowConsentIsRequired] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const [isEmailSuggestionModalVisible, setIsEmailSuggestionModalVisible] = useState(false);
    const [suggestedEmail, setSuggestedEmail] = useState<string>('');

    const displayEmailInput = props.displayEmailInput || props.variant !== NewsletterSubscriptionBlockVariant.OrderSummary;

    const clearApiResponse = () => {
        setErrorMessage('');
        setShowConsentIsRequired(false);
    };

    const handleSubmit = () => {
        clearApiResponse();

        if (!RegexProvider.email.test(email)) {
            return setErrorMessage(getTranslation(keys['newsletter.InvalidEmail']));
        }

        if (!consent) {
            return setShowConsentIsRequired(true);
        }

        if (displayEmailInput) {
            validateEmail().then((isValid) => isValid && subscribeEmailToNewsletter(email));
        } else {
            subscribeEmailToNewsletter(email);
        }
    };

    const validateEmail = (): Promise<boolean> => {
        setIsRequestActive(true);

        return new Promise<boolean>((resolve) => {
            postFor<EmailValidationResult>(props.checkEmailUrl, { emailAddress: email })
                .then((result: EmailValidationResult) => {
                    if (result.status === EmailValidationStatus.Deliverable) {
                        resolve(true);
                    }

                    if (result.suggestion || result.status === EmailValidationStatus.Undeliverable) {
                        setIsEmailSuggestionModalVisible(true);
                        setSuggestedEmail(result.suggestion);
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                })
                .catch(() => {
                    resolve(true);
                    setIsRequestActive(false);
                });
        });
    };

    const subscribeEmailToNewsletter = (emailToSubscribe: string) => {
        setIsRequestActive(true);
        postFor<NewsletterSubscriptionApiResponse>(signUpUrl, {
            userEmail: emailToSubscribe,
            subscriptionSource: props.model.subscriptionSource,
            acquisitionSource: props.model.acquisitionSource
        })
            .then((result) => {
                if (result.isSuccess && result.message !== 'AlreadySubscribed') {
                    setIsSuccess(true);
                }
                setErrorMessage(getTranslation(`newsletter.${result.message}`));
                setIsRequestActive(false);
            })
            .catch(() => {
                setIsRequestActive(false);
                setErrorMessage(getTranslation(keys[`newsletter.UnexpectedError`]));
            });
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === 'enter') {
            handleSubmit();
        }
    };

    const handleCheckboxOnChange = () => {
        if (isRequestActive) return;
        setConsent(!consent);
        !consent && setShowConsentIsRequired(false);
    };

    const rejectEmailSuggestion = () => {
        setIsEmailSuggestionModalVisible(false);
        subscribeEmailToNewsletter(email);
    };

    const acceptEmailSuggestion = () => {
        setIsEmailSuggestionModalVisible(false);

        if (suggestedEmail) {
            subscribeEmailToNewsletter(suggestedEmail);
        } else {
            setIsRequestActive(false);
        }
    };

    const newsletterBlockClassNames = classNames(
        'NewsletterBlock',
        isSuccess && 'NewsletterBlock--Success',
        BlockSectionClassResolver.Resolve(props.model.blockSectionMargin),
        `NewsletterBlock--${NewsletterSubscriptionBlockVariant[props.variant]}`
    );

    const newsletterBlockImage =
        props.variant === NewsletterSubscriptionBlockVariant.OrderSummary ? '/dist/assets/images/newsletter.svg' : '/dist/assets/images/mailbox.svg';

    return (
        <div className={newsletterBlockClassNames}>
            <div className="NewsletterBlock__Content">
                {!isSuccess ? (
                    <div className="NewsletterBlock__FormWrapper">
                        <h3 className="NewsletterBlock__Heading">
                            <RenderHTML html={headingText} />
                        </h3>
                        {subheadingText && (
                            <h4 className="NewsletterBlock__Subheading">
                                <RenderHTML html={subheadingText} />
                            </h4>
                        )}

                        <div className="NewsletterBlock__Form">
                            <div
                                className={classNames(
                                    'CustomCheckbox',
                                    props.variant !== NewsletterSubscriptionBlockVariant.OrderSummary && 'CustomCheckbox--White',
                                    'NewsletterBlock__Checkbox',
                                    showConsentIsRequired && 'NewsletterBlock__Checkbox--Error',
                                    isRequestActive && 'NewsletterBlock__Checkbox--Disabled'
                                )}
                            >
                                <input
                                    type="checkbox"
                                    id="newsletterConsent"
                                    name="newsletterConsent"
                                    checked={consent}
                                    onChange={handleCheckboxOnChange}
                                    aria-disabled={isRequestActive}
                                />
                                <label htmlFor="newsletterConsent">
                                    <RenderHTML html={consentText} />
                                </label>
                            </div>
                            {showConsentIsRequired && (
                                <span className="NewsletterBlock__CheckboxRequired">{!consent && getTranslation(keys['newsletter.LackOfConsent'])}</span>
                            )}

                            <div className="NewsletterBlock__InputWrapper">
                                {displayEmailInput && (
                                    <div className="NewsletterBlock__EmailInputWrapper">
                                        <EmailInput
                                            onEmailChange={(email) => setEmail(email)}
                                            onKeyUp={onKeyUp}
                                            errorMessage={errorMessage}
                                            onInput={() => setErrorMessage('')}
                                            showEmailSuggestionMessage={false}
                                            disabled={isRequestActive}
                                        />
                                    </div>
                                )}

                                <Button
                                    variant={ButtonVariant.Tertiary}
                                    size={ButtonSize.Large}
                                    loading={isRequestActive}
                                    className="NewsletterBlock__Button"
                                    onClick={handleSubmit}
                                >
                                    {getTranslation(keys['newsletter.SignUp'])}
                                </Button>
                            </div>

                            {!displayEmailInput && errorMessage && (
                                <span className="EmailInput__Message EmailInput__Message--Error EmailInput__Message--BigMarginTop">{errorMessage}</span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="NewsletterBlock__SuccessWrapper">
                        <div className="NewsletterBlock__SuccessCheck" />
                        <h3 className="NewsletterBlock__SuccessHeading">
                            <RenderHTML html={successHeading} />
                        </h3>
                        <div className="NewsletterBlock__SuccessMessage">
                            <RenderHTML html={successMessage} />
                        </div>
                    </div>
                )}

                <div className="NewsletterBlock__Image">
                    <img data-src={newsletterBlockImage} alt="Farmasiet Logo" className="lazyload" />
                </div>
            </div>

            <EmailSuggestionModal
                isVisible={isEmailSuggestionModalVisible}
                insertedEmail={email}
                suggestedEmail={suggestedEmail}
                onRejected={rejectEmailSuggestion}
                onAccepted={acceptEmailSuggestion}
                onCancelled={() => {
                    setIsEmailSuggestionModalVisible(false);
                    setIsRequestActive(false);
                }}
            />
        </div>
    );
};
